import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import omit from 'lodash/omit';
import { useContext, useState } from 'react';
import { Card } from 'react-bootstrap';
import Alert from 'react-bootstrap/Alert';
import Button from 'react-bootstrap/Button';
import Container from 'react-bootstrap/Container';
import Form from 'react-bootstrap/Form';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { z } from 'zod';

import { ApiError, AuthService, PasswordReset } from '../api';
import { FormFeedback } from '../app/components/FormFeedback';
import { Logo } from '../app/components/Logo';
import { ServerErrorsAlert } from '../app/components/ServerErrorsAlert';
import { FormSubmitContext } from '../app/contexts/FormSubmitContext';
import { useDocumentTitle } from '../app/hooks/use-document-title';

const validationSchema = z.object({
  email: z.string({ required_error: 'Email is required' }).email({
    message: "That email doesn't look quite right.",
  }),
});

export const ForgotPassword = () => {
  useDocumentTitle('Request password reset');
  const { isSubmitEnabled } = useContext(FormSubmitContext);
  const {
    handleSubmit,
    formState: { errors },
    register,
    clearErrors,
  } = useForm({
    resolver: zodResolver(validationSchema),
  });

  const [showSuccess, setShowSuccess] = useState(false);
  const [serverErrors, setServerErrors] = useState<ApiError>();
  const [showGlobalServerErrors, setShowGlobalServerErrors] = useState(false);
  const [showServerFieldErrors, setShowServerFieldErrors] = useState<{ email?: string }>({});

  // Callback for clearing alerts received from server
  function handleClearErrorAlert() {
    setShowGlobalServerErrors(false);
    setShowGlobalServerErrors(false);
    setServerErrors(undefined);
  }

  // Callback for clearing success messages received from server
  function handleClearSuccessAlert() {
    setShowSuccess(false);
  }

  const handleSubmitClick = () => {
    clearErrors();
    setShowGlobalServerErrors(false);
    setShowServerFieldErrors({});
  };

  const mutationForgotPassword = useMutation({
    mutationFn: async (data: PasswordReset) => {
      await AuthService.authPasswordResetCreate({ requestBody: data });
    },
  });

  const onSubmit = async (data) => {
    try {
      await mutationForgotPassword.mutateAsync(data);
      setShowSuccess(true);
    } catch (errors) {
      setServerErrors(errors);
      const fieldErrors = omit(errors.body, 'non_field_errors');
      setShowServerFieldErrors(
        // transform object of errors in object booleans (if field has errors, it will be true)
        Object.entries(fieldErrors).reduce((prev, [key]) => ({ ...prev, [key]: true }), {})
      );
      setShowGlobalServerErrors(true);
    }
  };

  return (
    <div className="login-bg-container">
      <Container>
        <Card>
          <Card.Body>
            <Form noValidate onSubmit={handleSubmit(onSubmit)}>
              <div className="logo-container">
                <Logo className="logo" variant="color" />
              </div>
              <Alert show={showSuccess} variant="success" onClose={() => handleClearSuccessAlert()}>
                <Alert.Heading>Request Received</Alert.Heading>
                <p>
                  If we have a record of your email address, you will receive an email with a link
                  to set a new password.
                </p>
              </Alert>
              {!showSuccess && (
                <div className="mb-3 small text-muted bg-primary-muted rounded p-2 text-center">
                  Did you forget your password or just wish to reset it?
                </div>
              )}
              {!showSuccess && (
                <>
                  <Form.Group className="mb-3">
                    <Form.Control
                      type="email"
                      placeholder="name@email.com"
                      {...register('email', {
                        onChange: () =>
                          setShowServerFieldErrors(omit(showServerFieldErrors, 'email')),
                      })}
                      isInvalid={!!errors.email || !!showServerFieldErrors.email}
                    />
                    <FormFeedback
                      clientError={errors.email}
                      serverErrors={showServerFieldErrors.email ? serverErrors?.body?.email : null}
                    />
                  </Form.Group>
                </>
              )}
              {!showSuccess && (
                <div className="d-grid">
                  <Button
                    size="lg"
                    className="btn-shadow w-100"
                    type="submit"
                    variant="primary"
                    onClick={() => handleSubmitClick()}
                    disabled={mutationForgotPassword.isLoading || !isSubmitEnabled}
                  >
                    Request Password Reset
                  </Button>
                </div>
              )}
              <div className="w-100 text-center mt-4">
                <Link to="/login">Back to Login</Link>
              </div>
            </Form>
            <ServerErrorsAlert
              errors={serverErrors}
              showErrors={showGlobalServerErrors}
              clearErrors={handleClearErrorAlert}
            />
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
};
